import axios from 'axios';

// API Service - use production URL by default, localhost for development
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (typeof window !== 'undefined' && 
   (window.location.hostname === 'sso.syberkey.com' || !window.location.hostname.includes('localhost'))
   ? 'https://api.syberkey.com' 
   : 'http://localhost:3000');

interface LoginResponse {
  success: boolean;
  token: string;
  user: {
    userId: string;
    email: string;
    fullName: string;
  };
  message: string;
}

class SyberKeyApi {
  private baseURL: string;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
  }

  async loginUser(email: string, password: string): Promise<LoginResponse> {
    try {
      const response = await axios.post(`${this.baseURL}/login/user`, {
        email,
        password
      });

      if (response.data.error) {
        throw new Error(response.data.error);
      }

      return response.data;
    } catch (error: any) {
      if (error.response?.data?.error) {
        throw new Error(error.response.data.error);
      }
      throw new Error('Login failed. Please try again.');
    }
  }

  async authenticateWithBiometric(uniqueCode: string, biometricData: string, apiKey: string, hmacSecret: string, tenantId: string, sessionId?: string) {
    try {
      // Generate timestamp and nonce for signature
      const timestamp = Date.now();
      const nonce = this.generateNonce();
      
      // Generate proper HMAC-SHA256 signature
      // Backend expects: JSON.stringify({ uniqueCode, qrBlob, timestamp, nonce })
      const payload = { uniqueCode, qrBlob: biometricData, timestamp, nonce };
      const payloadString = JSON.stringify(payload);
      const signature = await this.generateHMACSignature(payloadString, hmacSecret);

      // Get or generate session_id (mandatory)
      let session_id = sessionId;
      if (!session_id) {
        // Try to get from localStorage
        try {
          const sessionData = localStorage.getItem('syberkey_session');
          if (sessionData) {
            const session = JSON.parse(sessionData);
            session_id = session.session_id;
          }
        } catch (e) {
          // Ignore localStorage errors
        }
        
        // Generate new session_id if not found
        if (!session_id) {
          session_id = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
        }
      }

      const response = await axios.post(`${this.baseURL}/user-tenant-mapping/tenant/${tenantId}/authenticate`, {
        uniqueCode,
        qrBlob: biometricData,
        timestamp,
        nonce,
        signature,
        session_id // Mandatory session_id
      }, {
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': apiKey, // Public API key for authentication
          'x-tenant-id': tenantId
        }
      });

      // Store session_id in localStorage if received from API response
      if (response.data.session_id) {
        try {
          const sessionData = {
            session_id: response.data.session_id,
            login_id: response.data.login_id,
            expires_at: response.data.expires_at || Date.now() + (300 * 1000), // Default 5 minutes
            created_at: Date.now()
          };
          localStorage.setItem('syberkey_session', JSON.stringify(sessionData));
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Biometric authentication failed');
    }
  }

  private generateNonce(): string {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  /**
   * Generate HMAC-SHA256 signature using Web Crypto API (browser-compatible)
   * @param payload - The payload string to sign
   * @param secret - The HMAC secret key
   * @returns Hex-encoded HMAC signature
   */
  private async generateHMACSignature(payload: string, secret: string): Promise<string> {
    try {
      // Convert secret to ArrayBuffer
      const encoder = new TextEncoder();
      const keyData = encoder.encode(secret);
      const payloadData = encoder.encode(payload);

      // Import the key for HMAC
      const cryptoKey = await crypto.subtle.importKey(
        'raw',
        keyData,
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
      );

      // Sign the payload
      const signature = await crypto.subtle.sign('HMAC', cryptoKey, payloadData);

      // Convert ArrayBuffer to hex string
      const hashArray = Array.from(new Uint8Array(signature));
      const hashHex = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
      
      return hashHex;
    } catch (error) {
      console.error('Error generating HMAC signature:', error);
      throw new Error('Failed to generate signature');
    }
  }

  // Create SSO token for app access
  createSSOToken(userToken: string, appId: string): string {
    // In production, this would be handled server-side
    // For demo, we'll just encode the user token with app info
    const ssoData = {
      userToken,
      appId,
      timestamp: Date.now(),
      expires: Date.now() + (60 * 60 * 1000) // 1 hour
    };
    
    return btoa(JSON.stringify(ssoData));
  }

  // Validate SSO token
  validateSSOToken(ssoToken: string): boolean {
    try {
      const decoded = JSON.parse(atob(ssoToken));
      return decoded.expires > Date.now();
    } catch {
      return false;
    }
  }
}

export const syberKeyApi = new SyberKeyApi(BASE_URL);